import { useEffect, useMemo, useState } from "react";
import { trpc } from "../../lib/trpc";
import { useIsMobile } from "../../lib/useIsMobile";
import { Icon } from "../ui/Icon";
import { Drawer } from "../Drawer";
import { SearchSelect } from "../ui/SearchSelect";
import "./PersonnelAttendanceDrawer.css";

/**
 * ÜRETİM · Personel Devam Beyanı — TAM EKRAN, KİŞİ BAZLI.
 *
 * Sol panel: seçili (gün, vardiya) için POZİSYONA GÖRE gruplanmış kişi listesi. Kaynak
 * `shiftAttendance.dayBoard` — plan CANLI çözülür (haftalık pano ile aynı motor), ham
 * gün tablosu sorgulanmaz. Üretim sorumlusu hazır olanları işaretler; PLANLI olup
 * işaretlenmeyen kişi kaydederken devamsız YAZILIR (çıkarsanmaz).
 *
 * Sağ panel: pozisyon başına kişi sayısı. Sayı artık ELLE yazılmaz, işaretlenen
 * kişilerden TÜRETİLİR. Personel kaydı olmayan pozisyonlar için elle giriş korunur —
 * İK modülüne henüz girilmemiş kadro raporda yok olmasın diye.
 *
 * Tasarım dili haftalık vardiya panosundan alınır (çip + seçim kutusu + ad balonu, kenarlık
 * ve `--primary-subtle` seçili durumu): aynı işi yapan iki ekran farklı görünmesin. Sınıflar
 * KOPYALANMAZ, `.att-chip` altında yeniden ölçülür — oradaki çip yoğun ızgara için minik
 * (≈11px), buradaki ise dokunmatikte işaretlenir ve 44px hedefe uymak zorundadır.
 */

type ShiftLite = { id: string; name: string; startTime: string; endTime: string; sortOrder: number; isActive: boolean };

function ymdOf(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

/** İçinde bulunulan vardiya (Europe/Istanbul duvar saati) — resolveShiftId muadili. */
function currentShiftIdFrom(shifts: ShiftLite[]): string | null {
  if (shifts.length === 0) return null;
  const parts = new Intl.DateTimeFormat("tr-TR", { timeZone: "Europe/Istanbul", hour: "2-digit", minute: "2-digit", hour12: false }).formatToParts(new Date());
  const hh = Number(parts.find((p) => p.type === "hour")?.value ?? "0");
  const mm = Number(parts.find((p) => p.type === "minute")?.value ?? "0");
  const t = (hh % 24) * 60 + mm;
  const toMin = (s: string) => { const [h, m] = s.split(":"); return Number(h) * 60 + Number(m); };
  for (const s of [...shifts].sort((a, b) => a.sortOrder - b.sortOrder)) {
    const start = toMin(s.startTime), end = toMin(s.endTime);
    const inRange = start === end ? true : start < end ? t >= start && t < end : t >= start || t < end;
    if (inRange) return s.id;
  }
  return null;
}

const DURUM_ETIKET: Record<string, { label: string; icon: "clock" | "calendar" | "alertTriangle" }> = {
  planli: { label: "Planlı", icon: "clock" },
  izinli: { label: "İzinli / tatil", icon: "calendar" },
  baska_vardiya: { label: "Başka vardiyada", icon: "clock" },
  plansiz: { label: "Planda yok", icon: "alertTriangle" },
};

const nf = new Intl.NumberFormat("tr-TR");

/** Dakika → "1 sa 30 dk" (0 dakikada yalnız saat, 0 saatte yalnız dakika). */
function sureTr(dk: number): string {
  const s = Math.floor(dk / 60), d = dk % 60;
  if (s === 0) return `${d} dk`;
  return d === 0 ? `${s} sa` : `${s} sa ${d} dk`;
}

/**
 * Süre girişi — SAAT + DAKİKA ayrı seçim, dakika YALNIZ 15'in katları.
 *
 * Serbest metin/`number` alanı yerine seçim kutusu: kullanıcı 7 dk yazamaz, dolayısıyla
 * sunucudan hata almaz. Kural üç katmanda da AYNI (bu seçenek listesi · zod · DB CHECK) —
 * biri gevşetilirse diğerleri tutar.
 */
function SureSecici({ id, minutes, maxHours, onChange, label }: { id: string; minutes: number; maxHours: number; onChange: (dk: number) => void; label: string }) {
  const sa = Math.floor(minutes / 60), dk = minutes % 60;
  return (
    <span className="att-dur">
      <select
        className="form-select" aria-label={`${label} — saat`} value={sa}
        onChange={(e) => onChange(Math.min(maxHours * 60, Number(e.target.value) * 60 + dk))}
      >
        {Array.from({ length: maxHours + 1 }, (_, i) => <option key={i} value={i}>{i}</option>)}
      </select>
      <span className="att-dur-x" aria-hidden="true">sa</span>
      <select
        className="form-select" aria-label={`${label} — dakika`} value={dk}
        onChange={(e) => onChange(Math.min(maxHours * 60, sa * 60 + Number(e.target.value)))}
      >
        {[0, 15, 30, 45].map((m) => <option key={m} value={m}>{String(m).padStart(2, "0")}</option>)}
      </select>
      <span className="att-dur-x" aria-hidden="true">dk</span>
    </span>
  );
}

/**
 * "Listeden seç → süre gir" bölümü (fazla mesai ve eksik çalışma AYNI bileşen).
 *
 * Herkesin yanına bir süre kutusu koymak yerine SEÇEREK eklenir: bir vardiyada 40 kişi
 * varken 40 kutu, istisnayı (mesaiye kalan 3 kişi) görünmez hâle getirirdi. Aday listesi
 * YALNIZ "var" işaretlilerdir — gelmeyene saat yazılamaz (kural sunucuda ve DB'de de var).
 */
function SaatBolumu({ baslik, altBaslik, ikon, maxHours, deger, setDeger, adaylar, onDegisti }: {
  baslik: string;
  altBaslik: string;
  ikon: "clock" | "alertTriangle";
  maxHours: number;
  deger: Record<string, number>;
  setDeger: (f: (d: Record<string, number>) => Record<string, number>) => void;
  adaylar: Array<{ id: string; name: string; pos: string }>;
  onDegisti: () => void;
}) {
  const adMap = new Map(adaylar.map((a) => [a.id, a.name]));
  // Sıralama listedeki sırayı izler; satırlar yazarken zıplamasın.
  const satirlar = adaylar.filter((a) => a.id in deger);
  const eklenebilir = adaylar.filter((a) => !(a.id in deger));
  const toplam = satirlar.reduce((s, a) => s + (deger[a.id] ?? 0), 0);

  /**
   * Aday listesi POZİSYONA GÖRE gruplanır. Tek vardiyada 80 kişilik pozisyon olabiliyor;
   * düz bir listede aranan kişiyi bulmak kaydırmayla mümkün değildi. `SearchSelect` hem
   * aramayı hem grup başlıklarını zaten sağlıyor (uygulamanın her yerinde AYNI bileşen).
   */
  const secenekler = useMemo(() => {
    const m = new Map<string, Array<{ value: string; label: string }>>();
    for (const a of eklenebilir) {
      const list = m.get(a.pos) ?? [];
      list.push({ value: a.id, label: a.name });
      m.set(a.pos, list);
    }
    return [...m].map(([group, items]) => ({ group, items }));
  }, [eklenebilir]);

  return (
    <>
      <div className="att-sec-head sub">
        <h3><Icon name={ikon} size={12} /> {baslik}</h3>
        <span className="att-sec-sub">{toplam > 0 ? sureTr(toplam) : altBaslik}</span>
      </div>
      <div className="att-sum">
        {satirlar.map((a) => (
          <div key={a.id} className="att-hrow">
            <span className="att-hrow-nm" title={adMap.get(a.id)}>{adMap.get(a.id)}</span>
            <SureSecici
              id={a.id}
              label={`${adMap.get(a.id)} ${baslik}`}
              minutes={deger[a.id] ?? 0}
              maxHours={maxHours}
              onChange={(dk) => { onDegisti(); setDeger((d) => ({ ...d, [a.id]: dk })); }}
            />
            <button
              type="button" className="att-hrow-del" aria-label={`${adMap.get(a.id)} — ${baslik} satırını kaldır`}
              onClick={() => { onDegisti(); setDeger((d) => { const n = { ...d }; delete n[a.id]; return n; }); }}
            >
              <Icon name="x" size={13} />
            </button>
          </div>
        ))}
        {satirlar.length === 0 ? <p className="att-sum-empty">{altBaslik} — kişi eklenmedi.</p> : null}
        {/* `value` HEP null: bu bir "ekle" kutusu, seçim taşıyıcısı değil — seçilen kişi
            listeye satır olarak düşer ve kutu yeniden boşalır. */}
        <div className="att-hadd">
          <SearchSelect
            value={null}
            options={secenekler}
            disabled={eklenebilir.length === 0}
            clearable={false}
            icon="plus"
            placeholder={eklenebilir.length === 0 ? "Eklenecek personel yok — önce VAR işaretleyin" : `${baslik} personeli ekle…`}
            searchPlaceholder="Personel ara…"
            emptyText="Personel bulunamadı"
            onChange={(id) => {
              if (!id) return;
              onDegisti();
              setDeger((d) => ({ ...d, [id]: 60 })); // varsayılan 1 saat — en sık girilen değer
            }}
          />
        </div>
      </div>
    </>
  );
}

export function PersonnelAttendanceDrawer({ open, onClose, defaultCenterId, centers = [] }: { open: boolean; onClose: () => void; defaultCenterId?: string | null; centers?: Array<{ id: string; name: string }> }) {
  const qc = trpc.useUtils();
  const isMobile = useIsMobile();
  const wsQ = trpc.settings.workSchedule.get.useQuery(undefined, { enabled: open, staleTime: 60_000 });
  const shifts = useMemo<ShiftLite[]>(() => (wsQ.data?.shifts ?? []).filter((s) => s.isActive), [wsQ.data]);

  const [date, setDate] = useState(() => ymdOf(new Date()));
  const [shiftId, setShiftId] = useState<string>("");
  const [centerId, setCenterId] = useState<string>(""); // "" = merkez bağımsız (yalnız merkez-bağımsız pozisyonlar)
  const [present, setPresent] = useState<Set<string>>(new Set());
  const [overtime, setOvertime] = useState<Record<string, number>>({}); // dakika
  const [short, setShort] = useState<Record<string, number>>({}); // dakika
  const [vals, setVals] = useState<Record<string, string>>({}); // personelsiz pozisyonların elle sayısı
  const [msg, setMsg] = useState<{ tone: "success" | "danger"; text: string } | null>(null);

  // Varsayılan vardiya = içinde bulunulan (yoksa ilk aktif), vardiyalar gelince bir kez.
  useEffect(() => {
    if (open && !shiftId && shifts.length) setShiftId(currentShiftIdFrom(shifts) ?? shifts[0]!.id);
  }, [open, shifts, shiftId]);
  // Açılışta merkezi Üretim sayfasında seçili merkeze varsayıla (Tümü ise merkez bağımsız).
  useEffect(() => { if (open) setCenterId(defaultCenterId ?? ""); }, [open, defaultCenterId]);

  const args = { date, shiftId, centerId: centerId || null };
  const boardQ = trpc.shiftAttendance.dayBoard.useQuery(args, { enabled: open && !!shiftId });
  const sheetQ = trpc.shiftAttendance.sheet.useQuery(args, { enabled: open && !!shiftId });

  const board = boardQ.data;
  const people = useMemo(() => [...(board?.planned ?? []), ...(board?.others ?? [])], [board]);

  // Mevcut beyandan işaretleri doldur. HİÇ beyan yoksa PLANLI herkes ön-işaretli gelir:
  // olağan durum "planlanan geldi"dir; sorumlu yalnız İSTİSNAYI (gelmeyeni) kaldırır.
  useEffect(() => {
    if (!board) return;
    const s = new Set<string>();
    if (board.declared) {
      for (const p of people) if (p.present) s.add(p.employeeId);
    } else {
      for (const p of board.planned) s.add(p.employeeId);
    }
    setPresent(s);
    const ot: Record<string, number> = {}, sh: Record<string, number> = {};
    for (const p of people) {
      if (p.overtimeMinutes > 0) ot[p.employeeId] = p.overtimeMinutes;
      if (p.shortMinutes > 0) sh[p.employeeId] = p.shortMinutes;
    }
    setOvertime(ot);
    setShort(sh);
  }, [board, people]);

  // Personel kaydı OLMAYAN pozisyonların elle sayısı mevcut beyandan gelir.
  useEffect(() => {
    if (!sheetQ.data) return;
    const m: Record<string, string> = {};
    for (const p of sheetQ.data.positions) m[p.positionId] = p.headcount == null ? "" : String(p.headcount);
    setVals(m);
  }, [sheetQ.data]);

  const saveMut = trpc.shiftAttendance.saveMarks.useMutation();
  const saveSheetMut = trpc.shiftAttendance.save.useMutation();

  // ── Pozisyona göre gruplama ──
  const gruplar = useMemo(() => {
    const byPos = new Map<string, typeof people>();
    for (const p of people) {
      const k = p.positionId ?? "__none__";
      const arr = byPos.get(k) ?? [];
      arr.push(p);
      byPos.set(k, arr);
    }
    return (board?.positions ?? [])
      .map((pos) => ({ pos, kisiler: byPos.get(pos.id) ?? [] }))
      .filter((g) => g.kisiler.length > 0);
  }, [people, board]);

  /** Personel kaydı olmayan pozisyonlar — sayı elle girilir (türetilecek kimse yok). */
  const elleGirilenler = useMemo(() => {
    const kisili = new Set(people.map((p) => p.positionId).filter(Boolean) as string[]);
    return (sheetQ.data?.positions ?? []).filter((p) => !kisili.has(p.positionId));
  }, [people, sheetQ.data]);

  /**
   * Saat girilebilecek kişiler = VAR işaretli olanlar (gelmeyene saat yazılamaz).
   * Pozisyon adı da taşınır — seçim kutusu listeyi ona göre gruplar.
   */
  const varOlanlar = useMemo(() => {
    const posAd = new Map((board?.positions ?? []).map((p) => [p.id, p.title]));
    return people
      .filter((p) => present.has(p.employeeId))
      .map((p) => ({ id: p.employeeId, name: p.name, pos: (p.positionId && posAd.get(p.positionId)) || "Pozisyonsuz" }));
  }, [people, present, board]);

  const varSayisi = (kisiler: typeof people) => kisiler.filter((k) => present.has(k.employeeId)).length;
  const toplamVar = people.filter((p) => present.has(p.employeeId)).length;
  const planliYok = (board?.planned ?? []).filter((p) => !present.has(p.employeeId)).length;
  const elleToplam = elleGirilenler.reduce((s, p) => {
    const n = parseInt((vals[p.positionId] ?? "").trim(), 10);
    return s + (Number.isFinite(n) && n > 0 ? n : 0);
  }, 0);
  const cakisan = people.filter((p) => p.otherCenter).length;

  /** Gelmeyene saat yazılamaz — kişi "yok"a dönünce mesai/eksik satırı da düşer. */
  function saatleriTemizle(ids: string[]) {
    setOvertime((o) => { const n = { ...o }; for (const id of ids) delete n[id]; return n; });
    setShort((s) => { const n = { ...s }; for (const id of ids) delete n[id]; return n; });
  }
  // Güncelleyici (updater) SAF kalmalı: içinden başka setState çağırmak StrictMode'da iki kez
  // koşar ve saat satırlarını beklenmedik biçimde siler. Karar önce burada verilir.
  function toggle(employeeId: string) {
    setMsg(null);
    const kaldiriliyor = present.has(employeeId);
    if (kaldiriliyor) saatleriTemizle([employeeId]);
    setPresent((s) => {
      const n = new Set(s);
      if (kaldiriliyor) n.delete(employeeId); else n.add(employeeId);
      return n;
    });
  }
  function toggleGrup(kisiler: typeof people, hepsi: boolean) {
    setMsg(null);
    if (!hepsi) saatleriTemizle(kisiler.map((k) => k.employeeId));
    setPresent((s) => {
      const n = new Set(s);
      for (const k of kisiler) { if (hepsi) n.add(k.employeeId); else n.delete(k.employeeId); }
      return n;
    });
  }

  async function submit() {
    setMsg(null);
    try {
      const r = await saveMut.mutateAsync({
        date, shiftId, centerId: centerId || null, presentIds: [...present],
        // Yalnız DOLU satırlar gider; 0'lar sunucuda zaten "kaldır" olarak türetilir.
        overtime: Object.entries(overtime).filter(([id, m]) => m > 0 && present.has(id)).map(([employeeId, minutes]) => ({ employeeId, minutes })),
        short: Object.entries(short).filter(([id, m]) => m > 0 && present.has(id)).map(([employeeId, minutes]) => ({ employeeId, minutes })),
      });
      // Personel kaydı olmayan pozisyonların elle sayısı AYRI uçla yazılır — türetilen
      // sayılarla karışmasın diye yalnız o pozisyonlar gönderilir.
      const entries = elleGirilenler
        .map((p) => ({ positionId: p.positionId, raw: (vals[p.positionId] ?? "").trim() }))
        .filter((x) => x.raw !== "" && Number.isFinite(parseInt(x.raw, 10)) && parseInt(x.raw, 10) >= 0)
        .map((x) => ({ positionId: x.positionId, headcount: parseInt(x.raw, 10) }));
      if (entries.length > 0) await saveSheetMut.mutateAsync({ date, shiftId, centerId: centerId || null, entries });
      qc.shiftAttendance.invalidate();
      const ekler = [
        r.overtimeMinutes > 0 ? `${sureTr(r.overtimeMinutes)} mesai` : null,
        r.shortMinutes > 0 ? `${sureTr(r.shortMinutes)} eksik çalışma` : null,
        r.payrollWritten > 0 ? `${nf.format(r.payrollWritten)} puantaj satırı güncellendi` : null,
      ].filter(Boolean).join(" · ");
      setMsg({
        tone: "success",
        text: `Beyan kaydedildi — ${nf.format(r.present)} kişi var, ${nf.format(r.absent)} kişi yok.${ekler ? ` (${ekler})` : ""}${r.warnings.length ? ` ⚠ ${r.warnings.join(" ")}` : ""}`,
      });
    } catch (e) {
      setMsg({ tone: "danger", text: (e as Error).message });
    }
  }

  const shiftName = shifts.find((s) => s.id === shiftId)?.name ?? "";
  const busy = saveMut.isPending || saveSheetMut.isPending;

  return (
    <Drawer
      open={open}
      onClose={onClose}
      fullScreen
      dismissible={false}
      title="Personel Devam Beyanı"
      subtitle={shiftName ? `${date} · ${shiftName}` : "Vardiya bazında kişi devam beyanı"}
      footer={
        <div className="att-foot">
          <span className="att-foot-total">
            Var: <b className="mono">{nf.format(toplamVar + elleToplam)}</b>
            {planliYok > 0 ? <> · Yok: <b className="mono">{nf.format(planliYok)}</b></> : null}
          </span>
          <div className="att-foot-btns">
            <button type="button" className="btn-md ghost" onClick={onClose} disabled={busy}>Kapat</button>
            <button type="button" className="btn-md primary" onClick={submit} disabled={busy || !shiftId || (people.length === 0 && elleGirilenler.length === 0)}>
              {busy ? "Kaydediliyor…" : "Kaydet"}
            </button>
          </div>
        </div>
      }
    >
      {msg ? (
        <div className={msg.tone === "success" ? "form-banner-ok" : "form-banner-err"} role="status">{msg.text}</div>
      ) : null}

      {/* ── Süzgeç şeridi ── */}
      <div className="att-bar">
        <div className="form-field">
          <label className="lbl" htmlFor="att-date">Tarih</label>
          <input id="att-date" className="form-input" type="date" value={date} max={ymdOf(new Date())} onChange={(e) => { setDate(e.target.value); setMsg(null); }} />
        </div>
        <div className="form-field">
          <label className="lbl" htmlFor="att-shift">Vardiya</label>
          <select id="att-shift" className="form-select" value={shiftId} onChange={(e) => { setShiftId(e.target.value); setMsg(null); }}>
            {shifts.length === 0 ? <option value="">Vardiya tanımlı değil</option> : null}
            {shifts.map((s) => <option key={s.id} value={s.id}>{s.name} ({s.startTime}–{s.endTime})</option>)}
          </select>
        </div>
        {centers.length > 0 ? (
          <div className="form-field att-bar-wide">
            <label className="lbl" htmlFor="att-center">Üretim Merkezi</label>
            <select id="att-center" className="form-select" value={centerId} onChange={(e) => { setCenterId(e.target.value); setMsg(null); }}>
              <option value="">Merkez bağımsız (yalnız merkez-bağımsız pozisyonlar)</option>
              {centers.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
        ) : null}
      </div>

      {!shiftId ? (
        <div className="empty-state"><p className="ttl">Vardiya seçin</p><p className="sub">Çalışma Düzeni'nde vardiya tanımlı olmalı.</p></div>
      ) : boardQ.isLoading ? (
        <div className="empty-state"><p className="ttl">Yükleniyor…</p></div>
      ) : boardQ.isError ? (
        <div className="empty-state"><p className="ttl">Yüklenemedi</p><p className="sub">{boardQ.error.message}</p></div>
      ) : (
        <div className={isMobile ? "att-split m" : "att-split"}>
          {/* ── SOL: kişi panosu ── */}
          <section className="att-people">
            <div className="att-sec-head">
              <h3>Vardiyadaki Personel</h3>
              <span className="att-sec-sub">
                {nf.format((board?.planned ?? []).length)} planlı · {nf.format((board?.others ?? []).length)} diğer
              </span>
            </div>

            {cakisan > 0 ? (
              <div className="att-note warn" role="status">
                <Icon name="alertTriangle" size={13} />
                <span>{nf.format(cakisan)} kişi bu gün BAŞKA bir merkezin ekranından da beyan edilmiş — kaydederseniz bu ekranın verisi geçerli olur.</span>
              </div>
            ) : null}

            {gruplar.length === 0 ? (
              <div className="empty-state">
                <span className="ic" aria-hidden="true"><Icon name="users" size={28} /></span>
                <p className="ttl">Bu vardiyada personel yok</p>
                <p className="sub">{centerId ? "Bu merkezin beyana tabi pozisyonlarında aktif personel bulunamadı." : "Merkez-bağımsız pozisyonlarda aktif personel yok — üstten bir merkez seçin."}</p>
              </div>
            ) : gruplar.map(({ pos, kisiler }) => {
              const varN = varSayisi(kisiler);
              return (
                <div key={pos.id} className="att-grp">
                  <div className="att-grp-head">
                    <span className="att-grp-ttl">
                      {pos.title}
                      {pos.isShiftBased ? <span className="badge-pill b-info"><Icon name="clock" size={10} />Vardiyalı</span> : null}
                    </span>
                    <span className="att-grp-cnt mono" aria-label={`${pos.title} için ${varN} kişi var, toplam ${kisiler.length}`}>
                      {nf.format(varN)}/{nf.format(kisiler.length)}
                    </span>
                    <span className="att-grp-btns">
                      <button type="button" className="btn-md ghost xs" onClick={() => toggleGrup(kisiler, true)}>Tümü var</button>
                      <button type="button" className="btn-md ghost xs" onClick={() => toggleGrup(kisiler, false)}>Tümü yok</button>
                    </span>
                  </div>
                  <div className="att-chips">
                    {kisiler.map((k) => {
                      const secili = present.has(k.employeeId);
                      const meta = DURUM_ETIKET[k.durum] ?? DURUM_ETIKET.plansiz!;
                      return (
                        <button
                          key={k.employeeId}
                          type="button"
                          className={`att-chip${secili ? " picked" : ""}${k.planned ? "" : " unplanned"}`}
                          aria-pressed={secili}
                          onClick={() => toggle(k.employeeId)}
                          title={`${k.name} — ${meta.label}`}
                        >
                          <input className="pick" type="checkbox" checked={secili} readOnly tabIndex={-1} aria-hidden="true" />
                          <span className="nm">{k.name}</span>
                          {!k.planned ? <span className="ic" aria-hidden="true"><Icon name={meta.icon} size={11} /></span> : null}
                          <span className="pop">{k.code ? `${k.code} · ` : ""}{k.name} — {meta.label}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              );
            })}

            {board && (board.hidden.noPosition > 0 || board.hidden.otherScope > 0) ? (
              <div className="att-note" role="status">
                <Icon name="info" size={13} />
                <span>
                  Listede olmayan aktif personel:
                  {board.hidden.noPosition > 0 ? ` ${nf.format(board.hidden.noPosition)} kişi pozisyonsuz;` : ""}
                  {board.hidden.otherScope > 0 ? ` ${nf.format(board.hidden.otherScope)} kişi beyana tabi olmayan/başka merkezin pozisyonunda.` : ""}
                </span>
              </div>
            ) : null}
          </section>

          {/* ── SAĞ: pozisyon özeti ── */}
          <aside className="att-side">
            <div className="att-sec-head">
              <h3>Pozisyon Özeti</h3>
              <span className="att-sec-sub">İşaretlenen kişilerden türetilir</span>
            </div>
            <div className="att-sum">
              {gruplar.map(({ pos, kisiler }) => (
                <div key={pos.id} className="att-sum-row">
                  <span className="att-sum-ttl">{pos.title}</span>
                  <span className="att-sum-kadro mono" title="Kadro">{nf.format(pos.headcount)}</span>
                  <span className="att-sum-val mono">{nf.format(varSayisi(kisiler))}</span>
                </div>
              ))}
              {gruplar.length === 0 ? <p className="att-sum-empty">Türetilecek kişi yok.</p> : null}
            </div>

            {elleGirilenler.length > 0 ? (
              <>
                <div className="att-sec-head sub">
                  <h3>Personel Kaydı Olmayan Pozisyonlar</h3>
                  <span className="att-sec-sub">Sayı elle girilir</span>
                </div>
                <div className="att-sum">
                  {elleGirilenler.map((p) => (
                    <div key={p.positionId} className="att-sum-row">
                      <span className="att-sum-ttl">{p.title}</span>
                      <span className="att-sum-kadro mono" title="Kadro">{nf.format(p.plannedHeadcount)}</span>
                      <input
                        className="form-input att-sum-inp"
                        type="number"
                        min={0}
                        max={100000}
                        inputMode="numeric"
                        placeholder="0"
                        aria-label={`${p.title} çalışan kişi sayısı`}
                        value={vals[p.positionId] ?? ""}
                        onChange={(e) => setVals((v) => ({ ...v, [p.positionId]: e.target.value }))}
                      />
                    </div>
                  ))}
                </div>
              </>
            ) : null}

            {/* ── Fazla mesai / eksik çalışma — yalnız VAR işaretli kişiler ── */}
            <SaatBolumu
              baslik="Fazla Mesai"
              altBaslik="Puantaja mesai olarak yazılır"
              ikon="clock"
              maxHours={16}
              deger={overtime}
              setDeger={setOvertime}
              adaylar={varOlanlar}
              onDegisti={() => setMsg(null)}
            />
            <SaatBolumu
              baslik="Eksik Çalışma"
              altBaslik="Puantaja devamsızlık saati olarak yazılır"
              ikon="alertTriangle"
              maxHours={12}
              deger={short}
              setDeger={setShort}
              adaylar={varOlanlar}
              onDegisti={() => setMsg(null)}
            />

            <div className="att-note" role="status">
              <Icon name="info" size={13} />
              <span>
                Planlı olup işaretlenmeyen personel kaydederken <b>devamsız</b> olarak yazılır.
                Devamsızlık <b>kayda geçer</b>, ücret kesintisi yapılmaz — tam gün kesinti kararı İK'nın Puantaj ekranında verilir.
              </span>
            </div>
          </aside>
        </div>
      )}
    </Drawer>
  );
}
